import "./FirmwareUpdateModal.css";
import React, {useEffect, useState, useRef} from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { bulkUpgradeFirmwareApi } from "../../../services/api/firmware.api";
import { upgradeV1FirmwareApi, V1FirmwareChannel } from "../../../services/api/v1Firmware.api";
import { isV1Spa } from "../../../hooks/useSpaCommand";
import { useNavigate } from "react-router-dom";
import ModalShell, { ModalLoading, ModalTone } from "./ModalShell";
import { AlertIcon, CheckCircleIcon, ChevronRightIcon, MinusIcon } from "../Icons/Icons";

interface ModalProps {
    isOpen: boolean, 
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    spas: any[],
    firmwareType: string,
    version: string,
    /** V1 spas: the legacy catalog the version was picked from. */
    channel?: V1FirmwareChannel
}

const FirmwareUpdateModal: React.FC<ModalProps> = ({isOpen, setOpen, spas,firmwareType,version,channel='public'}) => {
    const navigate = useNavigate();
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const [response, setResponse] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const hasLoadedRef = useRef(false);

    const handleClose = () => {
        setOpen(false);
        setResponse(null);
        setLoading(false);
        hasLoadedRef.current = false;
    };

    useEffect(() => {
      if (isOpen && spas && spas.length > 0 && accessToken && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setLoading(true);
        setResponse(null);
        const upgradeAll = async () => {
          try {
            // Extract spa GUIDs from the spas array
            const spaGuids = spas.map(spa => spa.id);
            
            // V1 spas go through the legacy SpaServer; ConfirmFirmwareModal never lets V1 and V2 mix.
            const apiResponse = spas.every((spa) => isV1Spa(spa))
              ? await upgradeV1FirmwareApi(accessToken, {
                  spa_guids: spaGuids,
                  platform: firmwareType,
                  version: version,
                  channel,
                })
              : await bulkUpgradeFirmwareApi(accessToken, {
                  spa_guids: spaGuids,
                  platform: firmwareType,
                  version: version
                });

            setResponse(apiResponse);
            setLoading(false);
          } catch (error: any) {
            // If bulk upgrade fails entirely, set error response
            setResponse({
              data: {
                summary: {
                  results: {
                    errors: spas.map(spa => ({
                      spa_guid: spa.id,
                      status: "error",
                      message: error?.response?.data?.data?.message || error?.response?.data?.message || error?.message || "Bulk upgrade failed"
                    }))
                  }
                }
              }
            });
            setLoading(false);
          }
        };
        upgradeAll();
      }
    }, [isOpen, spas, firmwareType, version, channel, accessToken]);

    const formatMessage = (message: any) => {
      if (typeof message === "string" && message.includes("Firmware message published to the spa with guid:")) {
        return "Firmware message published to the spa.";
      }
      return typeof message === "string" ? message : "";
    };

  const renderResults = () => {
    if (!response) {
      return <p className="dlg-muted">No response data available</p>;
    }

    // Create a map of spa_guid to spa object for easy lookup
    const spaMap = new Map(spas.map(spa => [spa.id, spa]));
    const summary = response?.data?.summary;

    // Get counts
    const successCount = summary?.successful || 0;
    const errorCount = summary?.failed || 0;
    const skippedCount = summary?.skipped || 0;
    const totalCount = summary?.total || 0;

    const rows = [
      ...successRows.map((result: any) => ({ result, kind: "good", message: formatMessage(result.message) })),
      ...errorRows.map((result: any) => ({ result, kind: "bad", message: result.message || "Upgrade failed" })),
      ...skippedRows.map((result: any) => ({ result, kind: "skipped", message: formatMessage(result.message) })),
    ];

    return (
      <>
        {summary && (
          <div className="fwu-summary">
            {successCount > 0 && <span className="fwu-count good"><CheckCircleIcon/>{successCount} successful</span>}
            {errorCount > 0 && <span className="fwu-count bad"><AlertIcon/>{errorCount} failed</span>}
            {skippedCount > 0 && <span className="fwu-count"><MinusIcon/>{skippedCount} skipped</span>}
            <span className="fwu-count">{totalCount} total</span>
          </div>
        )}

        {response?.data?.message && <p>{response.data.message}</p>}

        <p className="dlg-muted">Click on a spa below to view details and track update progress.</p>

        {rows.length > 0 && (
          <ul className="fwu-results">
            {rows.map(({ result, kind, message }) => {
              const spa = spaMap.get(result.spa_guid);
              return (
                <li key={`${kind}-${result.spa_guid}`}>
                  <a
                    className={`fwu-result ${kind}`}
                    href={`/spa/${result.spa_guid}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/spa/${result.spa_guid}`);
                    }}
                  >
                    <span className="fwu-result-icon">
                      {kind === "good" ? <CheckCircleIcon/> : kind === "bad" ? <AlertIcon/> : <MinusIcon/>}
                    </span>
                    <span className="fwu-result-text">
                      <span className="fwu-result-name">{spa?.nick_name || result.spa_guid}</span>
                      {message && <span className="fwu-result-msg">{message}</span>}
                    </span>
                    <ChevronRightIcon className="fwu-result-chevron"/>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  };

  // Only rows for spas that were part of this request are shown
  const spaGuids = new Set(spas.map(spa => spa.id));
  const results = response?.data?.summary?.results;
  const successRows = results?.success?.filter((result: any) => spaGuids.has(result.spa_guid)) ?? [];
  const errorRows = results?.errors?.filter((result: any) => spaGuids.has(result.spa_guid)) ?? [];
  const skippedRows = results?.skipped?.filter((result: any) => spaGuids.has(result.spa_guid)) ?? [];

  const tone: ModalTone | undefined = loading || !response ? undefined
    : errorRows.length > 0 ? (successRows.length > 0 ? "warning" : "error")
    : "success";

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={loading ? "Requesting upgrade…" : tone === "error" ? "Upgrade requests failed" : "Upgrade requests sent"}
      subtitle={!loading && version ? `Firmware version ${version}` : undefined}
      tone={tone}
      actions={loading ? undefined : <button className="btn btn-primary" onClick={handleClose}>OK</button>}
    >
      {loading ? <ModalLoading/> : renderResults()}
    </ModalShell>
  );
};

export default FirmwareUpdateModal;
